import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Empty, Pagination, Spin, Tag, Tooltip, message, Modal, Button, Timeline, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';

const API_BASE = 'http://183.249.91.105:15600';
const AUTO_BOOKING_TASKS_API = `${API_BASE}/hpl/auto-booking-tasks`;
const DEFAULT_PER_PAGE = 20;

// const STATUS_TEXT = {
//     COMPLETED: '已完成',
//     STOPPED: '已停止',
// };

/** 根据状态关键字推断 Tag 颜色 */
const getStatusMeta = (status) => {
    const key = String(status || '').toUpperCase();
    if (['SUCCESS', 'COMPLETED'].some((k) => key.includes(k))) return { color: 'success' };
    if (['STOPPED'].some((k) => key.includes(k))) return { color: 'error' };
    if (['RUNNING'].some((k) => key.includes(k))) return { color: 'processing' };
    // if (['STOPPED'].some((k) => key.includes(k))) return { color: 'warning' };
    if (['DRAFT'].some((k) => key.includes(k))) return { color: 'default' };
    return { color: 'default' };
};

const formatDateTime = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(value);
};

const renderNumber = (value) => (value === null || value === undefined || value === '' ? '-' : value);

const renderPair = (a, b) => {
    if ((a === null || a === undefined) && (b === null || b === undefined)) return '-';
    return `${a ?? '-'} / ${b ?? '-'}`;
};
const normalizeResponse = (data) => {
    if (Array.isArray(data)) return { items: data, total: data.length };

    const items =
        (Array.isArray(data?.items) && data.items) ||
        (Array.isArray(data?.list) && data.list) ||
        (Array.isArray(data?.records) && data.records) ||
        (Array.isArray(data?.data) && data.data) ||
        [];

    const total = data?.total ?? data?.total_count ?? data?.totalCount ?? data?.count ?? data?.pagination?.total ?? items.length;

    return { items, total: Number(total) || 0 };
};
function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia(query);
        const handler = (event) => setMatches(event.matches);
        setMatches(mql.matches);
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else mql.removeListener(handler);
        };
    }, [query]);

    return matches;
}

const Field = ({ label, children, block }) => (
    <div
        style={{
            minWidth: 0,
            wordBreak: 'break-word',
            gridColumn: block ? '1 / -1' : undefined,
        }}
    >
        <strong>{label}：</strong>
        {children}
    </div>
);

const EllipsisText = ({ text, color }) => {
    if (text === null || text === undefined || text === '') return <span>-</span>;
    const content = String(text);
    return (
        <Tooltip
            title={
                <div style={{ maxWidth: 420, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {content}
                </div>
            }
            placement="topLeft"
        >
            <span
                style={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom',
                    color,
                }}
            >
                {content}
            </span>
        </Tooltip>
    );
};

export default function HplAutoBookingTaskList() {
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [loading, setLoading] = useState(false);

    const isMobile = useMediaQuery('(max-width: 768px)');
    const requestSeqRef = useRef(0);

    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [attempts, setAttempts] = useState([]);
    const [detailTask, setDetailTask] = useState(null);
    const [actionLoadingMap, setActionLoadingMap] = useState({});
    const fetchList = useCallback(async (targetPage, targetPerPage) => {
        const seq = ++requestSeqRef.current;
        setLoading(true);
        try {
            const res = await axios.get(AUTO_BOOKING_TASKS_API, {
                params: { page: targetPage, per_page: targetPerPage },
            });

            // 丢弃过期请求
            if (seq !== requestSeqRef.current) return;

            const { items, total: totalCount } = normalizeResponse(res.data);
            setList(items);
            setTotal(totalCount);
            if (items.length === 0 && totalCount > 0 && targetPage > 1) {
                const lastPage = Math.max(1, Math.ceil(totalCount / targetPerPage));
                if (lastPage !== targetPage) setPage(lastPage);
            }
        } catch (err) {
            if (seq !== requestSeqRef.current) return;
            console.error('获取自动订舱任务失败', err);
            message.error('获取自动订舱任务列表失败');
        } finally {
            if (seq === requestSeqRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchList(page, perPage);
    }, [page, perPage, fetchList]);

    const handlePageChange = (nextPage, nextPerPage) => {
        if (nextPerPage !== perPage) {
            setPerPage(nextPerPage);
            setPage(1);
            return;
        }
        setPage(nextPage);
    };

    const openDetailModal = (item) => {
        if (!item?.id) {
            message.error('任务 ID 缺失，无法查看详情');
            return;
        }

        setDetailTask(item);
        setAttempts([]);
        setDetailModalOpen(true);
        setDetailLoading(true);

        axios
            .get(`${AUTO_BOOKING_TASKS_API}/${item.id}/attempts`)
            .then((res) => {
                const data = res.data;
                const items =
                    (Array.isArray(data) && data) ||
                    (Array.isArray(data?.items) && data.items) ||
                    (Array.isArray(data?.data) && data.data) ||
                    [];
                setAttempts(items);
            })
            .catch((err) => {
                console.error('获取尝试记录失败', err);
                message.error('获取尝试记录失败');
            })
            .finally(() => setDetailLoading(false));
    };

    // 开始执行任务
    const handleStart = (id) => {
        if (!id) return;
        setActionLoadingMap((prev) => ({ ...prev, [id]: 'start' }));
        axios
            .post(`${AUTO_BOOKING_TASKS_API}/${id}/start`)
            .then(() => {
                message.success('任务已开始执行');
                fetchList(page, perPage);
            })
            .catch((err) => {
                console.error('开始执行失败', err);
                message.error(err?.response?.data?.message || '开始执行失败');
            })
            .finally(() => {
                setActionLoadingMap((prev) => ({ ...prev, [id]: null }));
            });
    };

    // 停止任务
    const handleStop = (id) => {
        if (!id) return;
        setActionLoadingMap((prev) => ({ ...prev, [id]: 'stop' }));
        axios
            .post(`${AUTO_BOOKING_TASKS_API}/${id}/stop`)
            .then(() => {
                message.success('任务已终止');
                fetchList(page, perPage); // 刷新列表状态
            })
            .catch((err) => {
                console.error('终止失败', err);
                message.error(err?.response?.data?.message || '终止失败');
            })
            .finally(() => {
                setActionLoadingMap((prev) => ({ ...prev, [id]: null }));
            });
    };

    // 自适应列数
    const gridColumns = isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))';

    return (
        <Card>
            <Spin spinning={loading}>
                {list.length === 0 && !loading ? (
                    <Empty description="暂无自动订舱任务" style={{ padding: '40px 0' }} />
                ) : (
                    list.map((item, index) => {
                        const status = item?.status;
                        const statusKey = String(status || '').toUpperCase();
                        const statusMeta = getStatusMeta(status);

                        return (
                            <div
                                key={item?.id ?? item?.trace_id ?? `${item?.quotation_number ?? 'row'}_${index}`}
                                style={{
                                    position: 'relative',
                                    border: '1px solid #aaa7a7',
                                    borderRadius: 4,
                                    padding: '12px 16px',
                                    marginBottom: 8,
                                    backgroundColor: '#fafafa',
                                    transition: 'border-color 0.2s ease',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#40a9ff')}
                                onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#aaa7a7')}
                            >
                                <div style={{ position: 'absolute', top: 10, right: 12, zIndex: 1, display: 'flex', gap: 8 }}>
                                    {/* DRAFT 状态显示开始执行 */}
                                    {statusKey === 'DRAFT' && (
                                        <Button
                                            size="small"
                                            type="primary"
                                            loading={actionLoadingMap[item.id] === 'start'}
                                            onClick={() => handleStart(item.id)}
                                        >
                                            开始执行
                                        </Button>
                                    )}

                                    {/* RUNNING 状态显示终止 */}
                                    {statusKey === 'RUNNING' && (
                                        <Popconfirm
                                            title="确定要终止该任务吗？"
                                            onConfirm={() => handleStop(item.id)}
                                            okText="确定"
                                            cancelText="取消"
                                        >
                                            <Button size="small" danger loading={actionLoadingMap[item.id] === 'stop'}>
                                                终止
                                            </Button>
                                        </Popconfirm>
                                    )}

                                    {/* 详情按钮一直存在 */}
                                    <Button size="small" onClick={() => openDetailModal(item)}>
                                        详情
                                    </Button>
                                </div>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: gridColumns,
                                        gap: '4px 16px',
                                        paddingRight: 72,
                                    }}
                                >
                                    <Field label="约号">{item?.quotation_number || '-'}</Field>
                                    <Field label="账号">{item?.booking_options?.account_name || '-'}</Field>
                                    <Field label="状态">
                                        {/* {item?.status || '-'} */}
                                        {status ? <Tag color={statusMeta.color}>{status}</Tag> : '-'}
                                    </Field>
                                    <Field label="成功数 / 目标数">{renderPair(item?.success_count, item?.target_success_count)}</Field>
                                    <Field label="已尝试 / 上限">{renderPair(item?.attempt_count, item?.max_attempts)}</Field>
                                    <Field label="失败次数">{renderNumber(item?.failure_count)}</Field>
                                    <Field label="间隔时间">
                                        {item?.interval_seconds === null ||
                                        item?.interval_seconds === undefined ||
                                        item?.interval_seconds === ''
                                            ? '-'
                                            : `${item.interval_seconds}s`}
                                    </Field>
                                    <Field label="下次执行时间">{formatDateTime(item?.next_run_at)}</Field>
                                    <Field label="创建时间">{formatDateTime(item?.created_at)}</Field>
                                    <Field label="开始时间">{formatDateTime(item?.started_at)}</Field>
                                    <Field label="完成时间">{formatDateTime(item?.finished_at)}</Field>
                                    <Field label="结束原因">
                                        <EllipsisText text={item?.stop_reason} />
                                    </Field>
                                    <Field label="最近一次错误">
                                        <EllipsisText text={item?.last_error} color={item?.last_error ? '#ff4d4f' : undefined} />
                                    </Field>
                                </div>
                            </div>
                        );
                    })
                )}
            </Spin>

            {total > 0 && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                        marginTop: 16,
                    }}
                >
                    <Pagination
                        current={page}
                        pageSize={perPage}
                        total={total}
                        showSizeChanger={!isMobile}
                        showQuickJumper={!isMobile}
                        simple={isMobile}
                        size={isMobile ? 'small' : 'default'}
                        pageSizeOptions={['10', '20', '50', '100']}
                        showTotal={isMobile ? undefined : (t) => `共 ${t} 条`}
                        onChange={handlePageChange}
                    />
                </div>
            )}
            <Modal
                title={`自动订舱详情${detailTask?.quotation_number ? `（${detailTask.quotation_number}）` : ''}`}
                open={detailModalOpen}
                onCancel={() => setDetailModalOpen(false)}
                footer={null}
                width={800}
                destroyOnClose
                maskClosable={false}
                styles={{
                    body: { maxHeight: '65vh', overflowY: 'auto', padding: '16px 24px' },
                }}
            >
                {detailLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin tip="正在加载详情..." />
                    </div>
                ) : attempts.length === 0 ? (
                    <Empty description="暂无记录" style={{ padding: '40px 0' }} />
                ) : (
                    <Timeline
                        style={{ marginTop: 16, paddingLeft: 4 }}
                        items={attempts.map((item) => {
                            // 根据状态判断颜色
                            const statusStr = String(item.status || '').toUpperCase();
                            const isSuccess = statusStr.includes('SUCCESS') || statusStr.includes('SUCCEEDED');
                            const isFail = statusStr.includes('FAIL') || statusStr.includes('ERROR');
                            // 使用 antd 规范的主题色
                            const dotColor = isSuccess ? '#52c41a' : isFail ? '#ff4d4f' : '#1677ff';

                            return {
                                dot: (
                                    <div
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: dotColor,
                                            border: '2px solid #fff',
                                            boxShadow: `0 0 0 2px ${dotColor}40`,
                                            marginTop: 18,
                                        }}
                                    />
                                ),
                                style: { paddingBottom: 16, paddingInlineStart: 8 },
                                children: (
                                    <div
                                        style={{
                                            border: '1px solid #d9d9d9',
                                            borderRadius: 8,
                                            padding: '14px 16px',
                                            backgroundColor: '#fff',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 8,
                                                marginBottom: 12,
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontWeight: 600, fontSize: 14, color: '#262626' }}>
                                                    第 {item.sequence_no} 次
                                                </span>
                                                <Tag
                                                    color={isSuccess ? 'success' : isFail ? 'error' : 'processing'}
                                                    style={{ margin: 0, borderRadius: 4 }}
                                                >
                                                    {item.status || '未知'}
                                                </Tag>
                                            </div>
                                            <span style={{ color: '#757474', fontSize: 12 }}>
                                                {formatDateTime(item.created_at)} ~ {formatDateTime(item.finished_at)}
                                            </span>
                                        </div>

                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                                gap: '8px 16px',
                                                fontSize: 13,
                                                color: '#595959',
                                            }}
                                        >
                                            <div>
                                                <span style={{ color: '#757474' }}>bookingnumber：</span>
                                                <strong style={{ color: '#262626' }}>{item.bookingnumber || '-'}</strong>
                                            </div>
                                            <div style={{ wordBreak: 'break-all' }}>
                                                <span style={{ color: '#757474' }}>trace_id：</span>
                                                <strong style={{ color: '#262626' }}>{item.trace_id || '-'}</strong>
                                            </div>
                                        </div>

                                        {/* 错误信息 (有内容才会展示) */}
                                        {item.error_message && (
                                            <div
                                                style={{
                                                    marginTop: 12,
                                                    padding: '8px 12px',
                                                    backgroundColor: '#fff1f0',
                                                    border: '1px solid #ffa39e',
                                                    borderRadius: 4,
                                                    color: '#cf1322',
                                                    fontSize: 13,
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                <strong>错误信息：</strong>
                                                {item.error_message}
                                            </div>
                                        )}
                                    </div>
                                ),
                            };
                        })}
                    />
                )}
            </Modal>
        </Card>
    );
}
